// Root layout — two responsive layouts:
//   - Phone (either orientation) → MobileLayout (single page + bottom tabs)
//   - Desktop                    → TopNav + split panels (page + chat)
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { KeyboardShortcutsDialog } from '@/components/KeyboardShortcutsDialog';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@onestone/ui/resizable';
import { UpdateBanner } from '@/components/UpdateBanner';
import { SWUpdateToast } from '@/components/SWUpdateToast';
import { apiFetch } from '@/lib/api';
import { useIsMobile } from '@/lib/useIsMobile';
import { useKeyboardShortcuts } from '@/lib/useKeyboardShortcuts';
import { TopNav } from './TopNav';
import { MobileLayout } from './MobileLayout';
import { SkipToMainContent } from './SkipToMainContent';
import { LoadingState } from '@onestone/ui/loading-state';

export function Layout() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [checked, setChecked] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const showHelp = useCallback(() => setShortcutsOpen(true), []);
  useKeyboardShortcuts({ onShowHelp: showHelp });

  // Check onboarding status on mount — redirect if not completed
  useEffect(() => {
    apiFetch(`/api/onboarding/status`)
      .then((r) => r.json())
      .then((data: { completed: boolean }) => {
        if (!data.completed) navigate('/onboarding', { replace: true });
        setChecked(true);
      })
      .catch(() => setChecked(true)); // Server not running — skip check
  }, [navigate]);

  if (!checked) {
    return <LoadingState variant="centered" />;
  }

  // ── Phone → single-page mobile shell ───────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <MobileLayout />
        <KeyboardShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      </>
    );
  }

  // ── Desktop → TopNav + split panels ────────────────────────────────────────
  return (
    <div data-testid="layout-desktop" className="flex h-full flex-col overflow-hidden bg-background text-foreground">
      <SkipToMainContent />
      <TopNav onShowShortcuts={showHelp} />
      <UpdateBanner />
      <SWUpdateToast />
      <main
        id="main-content"
        data-testid="main-content"
        tabIndex={-1}
        className="flex min-h-0 flex-1 overflow-hidden focus:outline-none"
      >
        {/* Side-by-side routed page + persistent chat. The chat pane has its
            own boundary (as the /chat route does): streaming chunks are the
            highest-risk render-crash source, and a crash there must leave the
            page and nav usable. */}
        <ResizablePanelGroup direction="horizontal" autoSaveId="onestone-layout-h2" className="flex min-h-0 flex-1">
          <ResizablePanel defaultSize={65} minSize={35} className="flex min-h-0 flex-col overflow-hidden">
            <Outlet />
          </ResizablePanel>
          {/* Drag-line only — no visible grip block. Hit area + keyboard
              resize stay (the Separator handles both); only the dotted
              decoration is dropped to keep the divider visually quiet. */}
          <ResizableHandle />
          <ResizablePanel defaultSize={35} minSize={20} className="min-h-0">
            <ErrorBoundary>
              <ChatPanel />
            </ErrorBoundary>
          </ResizablePanel>
        </ResizablePanelGroup>
      </main>
      <KeyboardShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
}
